<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '$shared/api';
  import { trackEvent } from '$lib/analytics.svelte';
  import { getCurrentRoute } from '$lib/router.svelte';
  import Avatar from '$shared/Avatar.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  interface SearchUser {
    id: number;
    username: string;
    avatar_url: string | null;
  }

  interface SearchDuel {
    id: number;
    name: string;
    genre_name: string;
    track_type: string;
    status: string;
  }

  interface SearchTrack {
    id: number;
    name: string;
    track_type: string;
    youtube_id: string | null;
    // Server nulls the submitter for live (non-ended) curation-duel entries —
    // anonymity backbone (docs/curation-duels.md § Anonymity). Guard before render.
    submitter: string | null;
    duel_name: string;
    duel_id: number;
    vote_count: number;
  }

  interface SearchResponse {
    users: SearchUser[];
    duels: SearchDuel[];
    tracks: SearchTrack[];
  }

  const RECENT_KEY = 'beatduel_recent_searches';
  const MAX_RECENT = 8;

  let query = $state('');
  let results = $state<SearchResponse | null>(null);
  let loading = $state(false);
  let searched = $state(false);
  let showRecent = $state(false);
  let recentSearches = $state<string[]>(loadRecent());
  let debounceTimer: ReturnType<typeof setTimeout>;
  let inputEl: HTMLInputElement | undefined = $state();

  const route = $derived(getCurrentRoute());

  // React to URL query changes (header search, direct navigation, back/forward)
  // untrack(query) avoids circular dependency: effect depends only on route.query
  $effect(() => {
    const urlQ = new URLSearchParams(route.query).get('q') || '';
    if (urlQ && urlQ !== untrack(() => query)) {
      query = urlQ;
      doSearch();
    }
  });

  function loadRecent(): string[] {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const list: string[] = raw ? JSON.parse(raw) : [];
      // Defense in depth: enforce the cap on load too, so existing stored
      // state beyond MAX_RECENT (from a prior larger cap, or external edits)
      // is normalized the moment it's read.
      return Array.isArray(list) ? list.slice(0, MAX_RECENT) : [];
    } catch {
      return [];
    }
  }

  function saveRecent(q: string) {
    const trimmed = q.trim();
    if (!trimmed) return;
    recentSearches = [trimmed, ...recentSearches.filter((s) => s !== trimmed)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recentSearches));
  }

  function removeRecent(q: string) {
    recentSearches = recentSearches.filter((s) => s !== q);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recentSearches));
  }

  function clearAllRecent() {
    recentSearches = [];
    localStorage.removeItem(RECENT_KEY);
  }

  function updateUrl(q: string) {
    const trimmed = q.trim();
    const base = '#/search';
    const newHash = trimmed ? `${base}?q=${encodeURIComponent(trimmed)}` : base;
    // Replace state to avoid polluting back history with every keystroke
    history.replaceState(null, '', newHash);
  }

  function handleInput() {
    clearTimeout(debounceTimer);
    showRecent = false;
    if (!query.trim()) {
      results = null;
      searched = false;
      updateUrl('');
      return;
    }
    debounceTimer = setTimeout(() => doSearch(), 350);
  }

  async function doSearch() {
    const q = query.trim();
    if (!q) return;
    loading = true;
    searched = true;
    showRecent = false;
    updateUrl(q);
    // Save to history BEFORE the API round-trip. Users expect their search
    // to be remembered whether or not the API succeeds (and whether or not
    // any side-effect like trackEvent throws).
    saveRecent(q);
    try {
      results = await api.get<SearchResponse>(`/search?q=${encodeURIComponent(q)}`);
      trackEvent('search', {
        metadata: {
          query: q,
          total_results: (results.users?.length ?? 0) + (results.duels?.length ?? 0) + (results.tracks?.length ?? 0),
        },
      });
    } catch {
      results = { users: [], duels: [], tracks: [] };
    }
    loading = false;
  }

  function handleSubmit(e: Event) {
    e.preventDefault();
    clearTimeout(debounceTimer);
    doSearch();
    inputEl?.blur();
  }

  function handleFocus() {
    if (!query.trim() && recentSearches.length > 0) {
      showRecent = true;
    }
  }

  function handleBlur() {
    // Delay to allow click on recent item
    setTimeout(() => {
      showRecent = false;
    }, 200);
  }

  function pickRecent(q: string) {
    query = q;
    showRecent = false;
    doSearch();
  }

  const totalResults = $derived(
    (results?.users.length ?? 0) + (results?.duels.length ?? 0) + (results?.tracks.length ?? 0),
  );
</script>

<div>
  <h1>Search</h1>
  <form class="search-form" onsubmit={handleSubmit}>
    <label for="search-input" class="sr-only">Search players, duels, or tracks</label>
    <div class="search-wrap">
      <input
        id="search-input"
        class="input search-input"
        type="search"
        placeholder="Search players, duels, or tracks..."
        bind:value={query}
        bind:this={inputEl}
        oninput={handleInput}
        onfocus={handleFocus}
        onblur={handleBlur}
        autocomplete="off"
      />

      {#if showRecent && recentSearches.length > 0}
        <div class="recent-dropdown">
          <div class="recent-header">
            <span class="recent-title">Recent</span>
            <button class="recent-clear" onclick={clearAllRecent}>Clear all</button>
          </div>
          {#each recentSearches as recent}
            <button class="recent-item" onmousedown={() => pickRecent(recent)}>
              <span class="recent-icon">&#128339;</span>
              <span class="recent-text">{recent}</span>
              <span
                class="recent-remove"
                role="button"
                tabindex="-1"
                onmousedown={(e: MouseEvent) => {
                  e.stopPropagation();
                  removeRecent(recent);
                }}
                aria-label="Remove {recent} from recent searches">&times;</span
              >
            </button>
          {/each}
        </div>
      {/if}
    </div>
  </form>

  {#if loading}
    <LoadingSkeleton lines={4} />
  {:else if results}
    <div class="search-results">
      {#if results.users.length > 0}
        <div class="search-section">
          <h2>Players</h2>
          {#each results.users as user}
            <a href="#/users/{user.username}" class="result-row">
              <Avatar username={user.username} avatarUrl={user.avatar_url} size="sm" />
              <span class="result-name">{user.username}</span>
            </a>
          {/each}
        </div>
      {/if}

      {#if results.duels.length > 0}
        <div class="search-section">
          <h2>Duels</h2>
          {#each results.duels as duel}
            <a href="#/duels/{duel.id}" class="result-row">
              <span class="result-name">{duel.name}</span>
              <span class="result-meta">{duel.genre_name} &middot; {duel.track_type} &middot; {duel.status}</span>
            </a>
          {/each}
        </div>
      {/if}

      {#if results.tracks.length > 0}
        <div class="search-section">
          <h2>Tracks</h2>
          {#each results.tracks as track}
            <a href="#/duels/{track.duel_id}?track={track.id}" class="result-row result-row--track">
              <div class="result-thumb">
                {#if track.youtube_id}
                  <img src="https://img.youtube.com/vi/{track.youtube_id}/mqdefault.jpg" alt="" loading="lazy" />
                {:else}
                  <div class="result-thumb-placeholder">&#9835;</div>
                {/if}
              </div>
              <div class="result-track-info">
                <span class="result-name">{track.name}</span>
                <span class="result-duel">{track.duel_name}</span>
                <span class="result-meta"
                  >{#if track.submitter}by {track.submitter} &middot;
                  {/if}{track.vote_count} vote{track.vote_count !== 1 ? 's' : ''}</span
                >
              </div>
            </a>
          {/each}
        </div>
      {/if}

      {#if totalResults === 0 && searched}
        <p class="empty">No results found for "{query}".</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  h1 {
    margin-bottom: 1.5rem;
  }
  h2 {
    font-size: 1rem;
    margin: 1.5rem 0 0.5rem;
    color: var(--text-secondary);
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .search-form {
    margin-bottom: 1rem;
  }
  .search-wrap {
    position: relative;
  }
  .search-input {
    width: 100%;
    font-size: 1rem;
    padding: 0.625rem 0.875rem;
  }
  .empty {
    color: var(--text-secondary);
    margin-top: 1rem;
  }
  .search-results {
    margin-top: 0.5rem;
  }
  .search-section {
    margin-bottom: 1rem;
  }
  .result-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.625rem 0.5rem;
    border-bottom: 1px solid var(--border);
    text-decoration: none;
    color: var(--text);
    transition: background 0.15s;
  }
  .result-row:hover {
    background: var(--surface);
  }
  .result-name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .result-meta {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-left: auto;
  }
  .result-row--track {
    gap: 0.75rem;
  }
  .result-thumb {
    width: 80px;
    height: 60px;
    flex-shrink: 0;
    border-radius: 6px;
    overflow: hidden;
    background: var(--surface-2);
  }
  .result-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .result-thumb-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: var(--text-muted);
  }
  .result-track-info {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    min-width: 0;
  }
  .result-duel {
    font-size: 0.8rem;
    color: var(--cyan);
    font-weight: 500;
  }
  .result-row--track .result-meta {
    margin-left: 0;
  }

  /* Recent searches dropdown */
  .recent-dropdown {
    position: absolute;
    left: 0;
    right: 0;
    top: 100%;
    z-index: 20;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 0 0 var(--radius) var(--radius);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }
  .recent-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--border);
  }
  .recent-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .recent-clear {
    font-size: 0.75rem;
    color: var(--cyan);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
  }
  .recent-clear:hover {
    text-decoration: underline;
  }
  .recent-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.5rem 0.75rem;
    background: none;
    border: none;
    border-bottom: 1px solid var(--border);
    color: var(--text);
    cursor: pointer;
    text-align: left;
    font-size: 0.875rem;
  }
  .recent-item:last-child {
    border-bottom: none;
  }
  .recent-item:hover {
    background: var(--surface-2);
  }
  .recent-icon {
    font-size: 0.875rem;
    color: var(--text-muted);
    flex-shrink: 0;
  }
  .recent-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .recent-remove {
    font-size: 1rem;
    color: var(--text-muted);
    background: none;
    border: none;
    cursor: pointer;
    padding: 0 0.25rem;
    line-height: 1;
    flex-shrink: 0;
  }
  .recent-remove:hover {
    color: var(--error);
  }
</style>
